import {LookupMapper} from '@/api/models/Feeds.ts';
import {ChangeFn} from '@/pages/feeds/Editor/Mapper/Mappers/index.tsx';
import {Toaster} from '@/toaster.ts';
import {Button, Classes, Dialog, FormGroup, HTMLTable, InputGroup, Tooltip} from '@blueprintjs/core';
import {ChangeEventHandler, EventHandler, ReactElement, SyntheticEvent, useCallback, useMemo, useState} from 'react';

type ValuePair = [string, string];

interface Props {
	mapper: LookupMapper,
	onChange: ChangeFn,
}

export default function LookupMapperEditor({mapper, onChange}: Props): ReactElement {
	const [showAddDialog, setShowAddDialog] = useState(false);

	const values: ValuePair[] = useMemo(() => {
		return Object.entries(mapper.valueMap);
	}, [mapper.valueMap]);

	const onAddClick = useCallback(() => {
		setShowAddDialog(true);
	}, []);

	const onAddDialogClose = useCallback(() => {
		setShowAddDialog(false);
	}, []);

	const onAddDialogSubmit: SubmitFn = useCallback((key, value) => {
		if (typeof mapper.valueMap[key] !== 'undefined')
			throw new DuplicateMappingError(key);

		mapper.valueMap[key] = value;

		onChange({
			...mapper,
			valueMap: {...mapper.valueMap},
		});

		setShowAddDialog(false);
	}, [mapper, onChange]);

	const onEntryDelete: DeleteFn = useCallback(key => {
		delete mapper.valueMap[key];

		onChange({
			...mapper,
			valueMap: {...mapper.valueMap},
		});
	}, [mapper, onChange]);

	return (
		<>
			<Button intent="primary" icon="add" text="Add" onClick={onAddClick} />

			{values.length > 0 && (
				<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
					<thead>
						<tr>
							<th>Value</th>
							<th>Replacement</th>
							<th>&nbsp;</th>
						</tr>
					</thead>

					<tbody>
						{values.map(([key, value]) => (
							<Row key={key} item={key} value={value} onDelete={onEntryDelete} />
						))}
					</tbody>
				</HTMLTable>
			)}

			{showAddDialog && (
				<AddLookupDialog onClose={onAddDialogClose} onSubmit={onAddDialogSubmit} />
			)}
		</>
	);
}

type DeleteFn = (key: string) => void;

interface RowProps {
	item: string,
	value: string,
	onDelete: DeleteFn,
}

function Row({item, value, onDelete}: RowProps): ReactElement {
	const onDeleteClick = useCallback(() => {
		onDelete(item);
	}, [item, onDelete]);

	return (
		<tr>
			<td>{item}</td>
			<td>{value}</td>
			<td style={{textAlign: 'right'}}>
				<Tooltip content="Delete">
					<Button intent="warning" variant="minimal" icon="delete" onClick={onDeleteClick} size="small" />
				</Tooltip>
			</td>
		</tr>
	);
}

type SubmitFn = (key: string, value: string) => void;

interface AddLookupDialogProps {
	onClose: () => void,
	onSubmit: SubmitFn,
}

function AddLookupDialog({onClose, onSubmit}: AddLookupDialogProps): ReactElement {
	const [key, setKey] = useState('');
	const [value, setValue] = useState('');

	const onKeyChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setKey(event.currentTarget.value);
	}, []);

	const onValueChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setValue(event.currentTarget.value);
	}, []);

	const onSubmitClick: EventHandler<SyntheticEvent> = useCallback(event => {
		event.preventDefault();

		try {
			onSubmit(key, value);
		} catch (error) {
			if (error instanceof DuplicateMappingError)
				Toaster.error(error.message);

			throw error;
		}
	}, [onSubmit, key, value]);

	return (
		<Dialog isOpen={true} onClose={onClose} title="Add Lookup">
			<form onSubmit={onSubmitClick}>
				<div className={Classes.DIALOG_BODY}>
					<FormGroup label="Original Value">
						<InputGroup onChange={onKeyChange} value={key} />
					</FormGroup>

					<FormGroup label="Mapped Value">
						<InputGroup onChange={onValueChange} value={value} />
					</FormGroup>
				</div>

				<div className={Classes.DIALOG_FOOTER}>
					<div className={Classes.DIALOG_FOOTER_ACTIONS}>
						<Button text="Cancel" onClick={onClose} />
						<Button type="submit" text="Save" intent="primary" onClick={onSubmitClick} />
					</div>
				</div>
			</form>
		</Dialog>
	);
}

class DuplicateMappingError extends Error {
	public constructor(key: string) {
		super(`A mapping already exists for key "${key}"`);
	}
}
